import { useLocalStorage } from "@uidotdev/usehooks";
import { AnimatePresence, motion } from "framer-motion";
import { ListFilterIcon, XIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

/** Whether a filter panel is open, remembered per panel like the Orders page. */
export function useFilterPanel(storageKey: string) {
	const [prefs, setPrefs] = useLocalStorage(storageKey, {
		isShowFilter: false,
	});
	return {
		open: prefs.isShowFilter,
		toggle: () => setPrefs((p) => ({ ...p, isShowFilter: !p.isShowFilter })),
	};
}

export function FilterToggle({
	open,
	activeCount,
	onToggle,
}: {
	open: boolean;
	activeCount: number;
	onToggle: () => void;
}) {
	const { t } = useTranslation("routing-mode");

	return (
		<Button
			type="button"
			variant={open ? "default" : "outline"}
			className="shadow-none"
			aria-expanded={open}
			onClick={onToggle}
		>
			<ListFilterIcon />
			{open ? t("filter.close") : t("filter.open")}
			{!open && activeCount > 0 && (
				<Badge variant="secondary" className="px-1.5">
					{activeCount}
				</Badge>
			)}
		</Button>
	);
}

export interface ActiveFilter {
	key: string;
	label: string;
	value: string;
	onClear: () => void;
}

/**
 * The portal's filter box: fields apply as they change, applied ones repeat as
 * dismissible "Active filters" pills (as on the Drafts page), Close and Clear all below.
 */
export function FilterPanel({
	open,
	onClose,
	onClearAll,
	activeFilters,
	children,
}: {
	open: boolean;
	onClose: () => void;
	onClearAll: () => void;
	activeFilters: ActiveFilter[];
	children: ReactNode;
}) {
	const { t } = useTranslation("routing-mode");

	return (
		<AnimatePresence initial={false}>
			{open && (
				<motion.section
					initial={{ opacity: 0, y: -20, filter: "blur(8px)" }}
					animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
					transition={{ duration: 0.3, ease: "easeOut" }}
					className="rounded-xl border bg-background p-3 shadow-inner"
				>
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-2 lg:grid-cols-4">
						{children}
					</div>

					{activeFilters.length > 0 && (
						<div className="mt-6 flex flex-wrap items-center gap-2">
							<span className="text-xs font-medium text-muted-foreground">
								{t("filter.active")}:
							</span>
							{activeFilters.map((filter) => (
								<Badge
									key={filter.key}
									variant="secondary"
									className="flex items-center gap-1 rounded-full py-1 pl-2 pr-1 text-[10px] uppercase tracking-wide"
								>
									<span>{filter.label}:</span>
									<span className="font-bold">{filter.value}</span>
									<button
										type="button"
										aria-label={t("filter.remove", { label: filter.label })}
										onClick={filter.onClear}
										className="inline-flex items-center justify-center rounded-full p-0.5 hover:bg-muted-foreground/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
									>
										<XIcon className="size-3" />
									</button>
								</Badge>
							))}
						</div>
					)}

					<div className="mt-4 flex flex-col justify-end gap-4 md:flex-row md:gap-2">
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="w-full md:w-fit"
							onClick={onClose}
						>
							{t("filter.close")}
						</Button>
						<Button
							type="button"
							variant="ghost-destructive"
							size="sm"
							className="w-full md:w-fit"
							onClick={onClearAll}
						>
							{t("filter.clear_all")}
						</Button>
					</div>
				</motion.section>
			)}
		</AnimatePresence>
	);
}
